import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2, CheckCircle2 } from 'lucide-react';

import { forgotPassword } from '@/store/actions/creators/authActions';
import {
  forgotPasswordEmailSendStatusSelector,
  sendForgotPasswordEmailErrorSelector,
} from '@/store/selectors/authSelector';
import { STARTED } from '@/utils/helpers/store-helpers';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const schema = z.object({
  email: z.string().trim().email({ message: 'Invalid email' }),
});

type FormValues = z.infer<typeof schema>;

export default function ForgotPassword() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [success, setSuccess] = useState(false);

  const sendError = useSelector(sendForgotPasswordEmailErrorSelector);
  const sendStatus = useSelector(forgotPasswordEmailSendStatusSelector);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '' },
  });

  const onSubmit = async (values: FormValues) => {
    try {
      // @ts-expect-error legacy redux-thunk action types
      await dispatch(forgotPassword(values.email.trim()));
      setSuccess(true);
    } catch {
      setSuccess(false);
    }
  };

  if (success) {
    return (
      <AuthLayout>
        <div className="flex flex-col items-center gap-3 py-4 text-center">
          <CheckCircle2 className="h-12 w-12 text-green-600" />
          <p className="text-sm">
            {t('FORGOT_PASSWORD.PLEASE_CHECK_YOUR_EMAIL_TO_RESET_YOUR_PASSWORD')}
          </p>
          <Link to="/login" className="text-sm text-primary hover:underline">
            {t('REGISTRATION.ALREADY_HAVE_ACCOUNT_SIGN_IN')}
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      description={t('FORGOT_PASSWORD.ENTER_YOUR_ADDRESS_AND_WE_WILL_SEND_YOU_A_PASSWORD_RESET_LINK')}
    >
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email">{t('COMMON.EMAIL')}</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            disabled={sendStatus === STARTED}
            {...form.register('email')}
          />
          {form.formState.errors.email && (
            <p className="text-sm text-destructive">
              {form.formState.errors.email.message}
            </p>
          )}
          {sendError && (
            <p className="text-sm text-destructive">{sendError}</p>
          )}
        </div>

        <Button type="submit" className="w-full" disabled={sendStatus === STARTED}>
          {sendStatus === STARTED && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {t('FORGOT_PASSWORD.SEND')}
        </Button>

        <p className="text-center text-sm text-muted-foreground">
          <Link to="/login" className="text-primary hover:underline">
            {t('REGISTRATION.ALREADY_HAVE_ACCOUNT_SIGN_IN')}
          </Link>
        </p>
      </form>
    </AuthLayout>
  );
}
